/**
 * Service-worker registration — the ONE registration path (PWA PR-C).
 *
 * The registration URL carries the build id (`/sw.js?v=<__BUILD_ID__>`) and
 * public/sw.js derives its cache name from that `v`. A deploy therefore
 * rolls caches end-to-end: fresh HTML → new `?v` → byte-different SW URL →
 * install (skipWaiting) → activate purges every stale `autoflo-*` cache.
 * No workbox — three cache behaviors don't justify a dependency.
 *
 * Update cadence — two distinct channels:
 * - NORMAL deploys propagate via the next NAVIGATION: fresh HTML carries a
 *   new `?v`, registering a byte-different SW URL. For the installed app
 *   that's the next launch/reload.
 * - The visibilitychange + 60-minute registration.update() below re-checks
 *   the CURRENT `?v` URL, whose bytes only change when sw.js itself is
 *   edited in place — i.e. the committed kill-switch
 *   (scripts/sw-kill-switch.js, docs/pwa.md). Update checks bypass the HTTP
 *   cache for the script (spec behavior), so a stale cached shell cannot
 *   pin the old SW: the kill switch lands on a glance, minutes not days.
 *
 * Admin host: no-op. SW registration is ORIGIN-scoped, so this is a pure
 * hostname test — deliberately NARROWER than src/App.tsx's admin gate,
 * whose DEV-only `/admin/` path escape answers a different question
 * ("render the admin shell?"), not "is this the admin origin?".
 *
 * push.ts reaches the registration via swReady() — never register('/sw.js')
 * anywhere else; a second registration URL would split update behavior
 * between two SW instances.
 */

const UPDATE_INTERVAL_MS = 60 * 60 * 1000

let registrationPromise: Promise<ServiceWorkerRegistration> | null = null

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return
  if (window.location.hostname.startsWith('admin.')) return
  if (registrationPromise) return

  // Register after `load` — never compete with first paint. main.tsx calls
  // this during module evaluation, which always precedes `load` (module
  // execution delays it), so the listener path is the live one; the
  // readyState check keeps the export safe for any future post-load caller,
  // where a bare listener would silently never fire.
  registrationPromise = new Promise((resolve, reject) => {
    const go = () => register().then(resolve, reject)
    if (document.readyState === 'complete') go()
    else window.addEventListener('load', go, { once: true })
  })
  // The app itself never awaits registration (register() already logs its
  // own failure) — this keeps a boot failure from surfacing as an unhandled
  // rejection. swReady() callers still receive the real rejection.
  registrationPromise.catch(() => {})
}

/**
 * The registration this module owns. Rejects when registration is
 * impossible (unsupported browser, admin host, not yet requested), failed
 * at boot, or a FIRST install dies (install's precache throws → the worker
 * goes `redundant` with nothing active — `navigator.serviceWorker.ready`
 * would pend forever by spec, it never rejects). Callers get the real error
 * instead of a hang. A slow first install simply waits — no false timeout.
 */
export function swReady(): Promise<ServiceWorkerRegistration> {
  if (!registrationPromise) {
    return Promise.reject(new Error('sw_not_registered'))
  }
  return registrationPromise.then((registration) => {
    if (registration.active) return registration
    const installing = registration.installing ?? registration.waiting
    // No active AND no installing/waiting worker is not a legitimate state:
    // register() resolves only after `installing` is set, and any successful
    // progression leaves `active` non-null — all-slots-null means a first
    // install already died (and the spec cleared the registration). Falling
    // back to navigator.serviceWorker.ready here would pend forever.
    if (!installing) return Promise.reject(new Error('sw_install_failed'))
    return new Promise<ServiceWorkerRegistration>((resolve, reject) => {
      const onState = () => {
        if (installing.state === 'activated') {
          installing.removeEventListener('statechange', onState)
          resolve(registration)
        } else if (installing.state === 'redundant' && !registration.active) {
          installing.removeEventListener('statechange', onState)
          reject(new Error('sw_install_failed'))
        }
      }
      installing.addEventListener('statechange', onState)
      onState()
    })
  })
}

async function register(): Promise<ServiceWorkerRegistration> {
  try {
    const registration = await navigator.serviceWorker.register(
      `/sw.js?v=${__BUILD_ID__}`,
    )
    const update = () => {
      // update() rejects on transient network failure — irrelevant, the
      // next glance retries.
      void registration.update().catch(() => {})
    }
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') update()
    })
    window.setInterval(update, UPDATE_INTERVAL_MS)
    return registration
  } catch (err) {
    // A failed registration must never break the app — the SPA works
    // SW-less exactly as before PR-C. Rethrown so swReady() callers see it.
    console.error(
      JSON.stringify({
        event: 'sw.register_failed',
        error: err instanceof Error ? err.message : String(err),
      }),
    )
    throw err
  }
}
